// Settings page — real SaaS account UX.
// Sections:
//   1. Profile          — full name, email, avatar (initials), connected providers
//   2. Plan & Billing   — current plan, usage, manage subscription (placeholder until billing wires)
//   3. Security         — change password (ONLY if signed in via email/password; hidden for Google)
//   4. Danger zone      — sign out, delete account (placeholder)
//
// Auth-gated: anonymous users get bounced to /login.
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  User as UserIcon, Mail, Shield, CreditCard, LogOut, ArrowLeft, Loader2, Check,
  Sparkles, Crown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleIcon } from "@/components/GoogleIcon";
import { useAuth } from "@/context/AuthContext";
import { useBilling } from "@/hooks/useBilling";
import { supabase } from "@/lib/supabase";

function Section({ icon: Icon, title, description, children }) {
  return (
    <section className="rounded-2xl border border-border bg-card">
      <div className="border-b border-border px-6 py-4">
        <div className="flex items-center gap-2.5">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-secondary text-foreground">
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <h2 className="text-[14.5px] font-semibold tracking-tight text-foreground">{title}</h2>
            {description && (
              <p className="mt-0.5 text-[12.5px] text-muted-foreground">{description}</p>
            )}
          </div>
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </section>
  );
}

function initialsFor(user) {
  const name = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || "";
  const parts = name.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] || "?").toUpperCase() + (parts[1]?.[0] || "").toUpperCase();
}

export default function SettingsPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { billing, isPro } = useBilling();

  // Profile form
  const [name, setName] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);

  // Password change
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [savingPwd, setSavingPwd] = useState(false);

  useEffect(() => { document.title = "Settings — MACHA"; }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/settings", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) setName(user.user_metadata?.full_name || user.user_metadata?.name || "");
  }, [user?.id]); // eslint-disable-line

  // Identify the auth provider — Google identities don't have a usable password
  // path, so we hide the "change password" section for them.
  const providers = useMemo(() => {
    const ids = user?.identities || [];
    return Array.from(new Set(ids.map((i) => i.provider)));
  }, [user]);
  const isGoogleOnly = providers.length > 0 && providers.every((p) => p === "google");
  const hasPasswordProvider = providers.includes("email") || providers.length === 0;

  if (loading || !user) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    const { error } = await supabase.auth.updateUser({ data: { full_name: name.trim() } });
    setSavingProfile(false);
    if (error) toast.error(error.message);
    else toast.success("Profile updated");
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (newPwd !== confirmPwd) { toast.error("Passwords don't match"); return; }
    setSavingPwd(true);
    // Supabase: must re-authenticate via signInWithPassword to be safe-ish,
    // but updateUser({password}) only needs an active session.
    const { error } = await supabase.auth.updateUser({ password: newPwd });
    setSavingPwd(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Password updated");
    setCurrentPwd(""); setNewPwd(""); setConfirmPwd("");
  };

  const sendPasswordReset = async () => {
    const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
      redirectTo: `${window.location.origin}/auth/callback?next=/settings`,
    });
    if (error) toast.error(error.message);
    else toast.success(`Password-reset link sent to ${user.email}`);
  };

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-12 sm:px-8">
      <div className="mb-6 flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="h-9 gap-1.5 rounded-lg px-2 text-muted-foreground hover:text-foreground">
          <Link to="/workspace"><ArrowLeft className="h-4 w-4" /> Back to workspace</Link>
        </Button>
      </div>
      <header className="mb-8">
        <h1 className="font-display text-3xl font-semibold tracking-tight text-foreground">Settings</h1>
        <p className="mt-2 text-[14px] text-muted-foreground">Manage your account, plan and security.</p>
      </header>

      <div className="space-y-5">
        {/* Profile */}
        <Section icon={UserIcon} title="Profile" description="Your account identity across MACHA.">
          <div className="mb-5 flex items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[16px] font-semibold text-primary">
              {initialsFor(user)}
            </div>
            <div className="min-w-0">
              <p className="truncate text-[14px] font-medium text-foreground" data-testid="settings-current-name">
                {user.user_metadata?.full_name || user.user_metadata?.name || "Unnamed"}
              </p>
              <p className="truncate text-[12.5px] text-muted-foreground" data-testid="settings-current-email">
                {user.email}
              </p>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {providers.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2 py-0.5 text-[10.5px] font-medium text-muted-foreground"
                  >
                    {p === "google" ? <GoogleIcon className="h-3 w-3" /> : <Mail className="h-3 w-3" />}
                    Connected via {p === "google" ? "Google" : "Email"}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <form onSubmit={saveProfile} className="space-y-4" aria-label="Profile form">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-[12.5px]">Full name</Label>
              <Input
                id="name" type="text" autoComplete="name"
                value={name} onChange={(e) => setName(e.target.value)}
                data-testid="settings-name-input"
                className="h-10 rounded-lg"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-[12.5px]">Email</Label>
              <Input
                id="email" type="email" value={user.email} disabled
                className="h-10 rounded-lg bg-background/50 text-muted-foreground"
              />
              <p className="text-[11px] text-muted-foreground">Email is locked to your auth provider.</p>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit" disabled={savingProfile}
                data-testid="settings-save-profile-btn"
                className="h-9 gap-1.5 rounded-lg"
              >
                {savingProfile ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</> : <><Check className="h-3.5 w-3.5" /> Save changes</>}
              </Button>
            </div>
          </form>
        </Section>

        {/* Plan */}
        <Section icon={CreditCard} title="Plan & billing" description="Your current plan and usage.">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <span className={[
                "inline-flex h-10 w-10 items-center justify-center rounded-lg",
                isPro ? "bg-emerald-500/15 text-emerald-500" : "bg-primary/15 text-primary",
              ].join(" ")}>
                {isPro ? <Crown className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
              </span>
              <div>
                <p className="text-[14px] font-semibold text-foreground" data-testid="settings-plan-name">
                  {isPro ? "Pro plan" : "Starter (Free)"}
                </p>
                {isPro && billing?.pro_expires_at && (
                  <p className="text-[12px] text-muted-foreground" data-testid="settings-plan-expires">
                    Active until {new Date(billing.pro_expires_at).toLocaleDateString()}
                  </p>
                )}
                {!isPro && (
                  <p className="text-[12px] text-muted-foreground" data-testid="settings-plan-usage">
                    {billing
                      ? `${billing.messages_used}/${billing.message_limit} messages used this month`
                      : "Includes 10 AI messages per month."}
                  </p>
                )}
              </div>
            </div>
            {!isPro && (
              <Button asChild className="h-9 gap-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/pricing" data-testid="settings-upgrade-btn"><Crown className="h-3.5 w-3.5" /> Upgrade to Pro</Link>
              </Button>
            )}
          </div>
        </Section>

        {/* Security — only for email/password users */}
        {hasPasswordProvider && !isGoogleOnly && (
          <Section icon={Shield} title="Security" description="Change your password or send yourself a reset link.">
            <form onSubmit={changePassword} className="space-y-4" aria-label="Change password form">
              <div className="space-y-1.5">
                <Label htmlFor="new-pwd" className="text-[12.5px]">New password</Label>
                <Input
                  id="new-pwd" type="password" autoComplete="new-password" minLength={8}
                  placeholder="At least 8 characters"
                  value={newPwd} onChange={(e) => setNewPwd(e.target.value)} required
                  data-testid="settings-new-password-input"
                  className="h-10 rounded-lg"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pwd" className="text-[12.5px]">Confirm new password</Label>
                <Input
                  id="confirm-pwd" type="password" autoComplete="new-password" minLength={8}
                  value={confirmPwd} onChange={(e) => setConfirmPwd(e.target.value)} required
                  data-testid="settings-confirm-password-input"
                  className="h-10 rounded-lg"
                />
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:justify-between">
                <Button
                  type="button" variant="ghost"
                  onClick={sendPasswordReset}
                  data-testid="settings-forgot-password-btn"
                  className="h-9 rounded-lg text-[13px] text-muted-foreground hover:text-foreground"
                >
                  Forgot? Email me a reset link
                </Button>
                <Button
                  type="submit" disabled={savingPwd || !newPwd || !confirmPwd}
                  data-testid="settings-change-password-btn"
                  className="h-9 gap-1.5 rounded-lg"
                >
                  {savingPwd ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Updating…</> : "Update password"}
                </Button>
              </div>
            </form>
          </Section>
        )}

        {isGoogleOnly && (
          <Section icon={Shield} title="Security" description="You signed in with Google.">
            <p className="text-[13px] leading-relaxed text-muted-foreground">
              Your password is managed by Google. Go to{" "}
              <a href="https://myaccount.google.com/security" target="_blank" rel="noreferrer"
                className="text-foreground underline-offset-4 hover:text-primary hover:underline">
                Google account security
              </a>{" "}
              to change it.
            </p>
          </Section>
        )}

        {/* Sign out */}
        <Section icon={LogOut} title="Session" description="Sign out of this device.">
          <Button
            variant="outline"
            data-testid="settings-signout-btn"
            onClick={async () => { await supabase.auth.signOut(); navigate("/", { replace: true }); }}
            className="h-9 gap-1.5 rounded-lg text-[13px]"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </Button>
        </Section>
      </div>
    </div>
  );
}
